import { copyToClipboard } from '@thoughtspot/browser-util';
import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import { Alert, BannerAlertType, } from '@thoughtspot/radiant-react/widgets/alert';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { Input } from '@thoughtspot/radiant-react/widgets/input';
import { Horizontal, Vertical, } from '@thoughtspot/radiant-react/widgets/layout';
import { Link } from '@thoughtspot/radiant-react/widgets/link';
import { Modal, ModalSize } from '@thoughtspot/radiant-react/widgets/modal';
import { Typography } from '@thoughtspot/radiant-react/widgets/typography';
import { getAccessToken, getCurrentOrgId, getScimBaseUrl, getUserPrivileges, isScimPerOrgEnabled, } from '@thoughtspot/session-service';
import { getIdpDomain } from '@thoughtspot/system-config-service';
import React, { useCallback, useEffect, useState } from 'react';
import { LearnMore, LearnMoreConfig, } from "../../../../../system/common/constants";
import { useCreateScimTokenMutation, useGetScimAuthTokenLazyQuery, } from '../../../../../../services/saml/saml-service';
import { TranslationsKeys } from '../../../../../authentication/constants';
import { useProvisioningMutation } from '../../../../../authentication/provisioning/useProvisioningCustomHook';
import styles from './settings-modal.module.scss';
export const SettingsModal = ({ onAction }) => {
    const { t } = useTranslation();
    const [nonce, setNonce] = useState('');
    const [tokenUrl, setTokenUrl] = useState('');
    const [isToastVisible, setIsToastVisible] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [urlCopied, setUrlCopied] = useState(false);
    const [tokenCopied, setTokenCopied] = useState(false);
    const [showRegenerateWarning, setShowRegenerateWarning] = useState(false);
    const [showRegenerateError, setShowRegenerateError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [settingsChanged, setSettingsChanged] = useState(false);
    const scimBaseUrl = `${getScimBaseUrl()}/scim/v2/`;
    const learnMoreKey = LearnMore.INTERNAL_AUTH;
    const learnMore = LearnMoreConfig[learnMoreKey] || undefined;
    const getDefaultScimHeaders = () => ({
        accessToken: `Bearer ${getAccessToken()}`,
        xTsPrivileges: btoa(JSON.stringify(getUserPrivileges())),
        xTsIdpDomain: getIdpDomain(),
    });
    const { createScimToken, error, data, loading, } = useCreateScimTokenMutation();
    const { fetchSignedToken: createSignedScimToken, error: authError, data: authData, loading: authLoading, } = useProvisioningMutation();
    const { getScimToken, error: getScimTokenError, ScimTokenAuthData, loading: getScimTokenLoading, } = useGetScimAuthTokenLazyQuery();
    const generateSignedToken = useCallback((nonceValue) => {
        createSignedScimToken(window.location.hostname, nonceValue, `${getCurrentOrgId()}`, getDefaultScimHeaders(), isScimPerOrgEnabled(), getScimBaseUrl());
    }, []);
    useEffect(() => {
        if (!getScimTokenLoading && (ScimTokenAuthData || getScimTokenError)) {
            if (ScimTokenAuthData.token) {
                setShowRegenerateWarning(false);
                setNonce(ScimTokenAuthData?.token);
            }
        }
    }, [ScimTokenAuthData, getScimTokenError, getScimTokenLoading]);
    useEffect(() => {
        if (!loading && (data || error)) {
            if (error) {
                setShowRegenerateError(true);
                setErrorMessage(error?.message);
            }
            else if (data?.Error) {
                setShowRegenerateError(true);
                setErrorMessage(data?.Error);
                setIsToastVisible(false);
                setShowRegenerateWarning(false);
            }
            else {
                setTokenCopied(false);
                setIsToastVisible(true);
                setToastMessage(t(TranslationsKeys.PROVISIONING_GENERATE_SUCCESS));
                setNonce(data?.Data?.token);
                generateSignedToken(data?.Data?.token);
                setSettingsChanged(true);
            }
        }
    }, [data, error, loading]);
    useEffect(() => {
        if (!authLoading && (authData || authError)) {
            setIsLoading(false);
            if (authError) {
            }
            else {
                setTokenUrl(authData?.token);
                setIsLoading(false);
                setIsToastVisible(true);
                setToastMessage(t(TranslationsKeys.PROVISIONING_GENERATE_SUCCESS));
            }
        }
    }, [authData, authError, authLoading]);
    useEffect(() => {
        getScimToken('primary');
    }, []);
    const handleCopyUrl = () => {
        copyToClipboard(scimBaseUrl, () => {
            setToastMessage(t(TranslationsKeys.SETTINGS_SCIM_BASEURL_COPIED_SUCCESS));
            setIsToastVisible(true);
        });
        setUrlCopied(true);
    };
    const handleCopyToken = () => {
        copyToClipboard(tokenUrl, () => {
            setToastMessage(t(TranslationsKeys.PROVISIONING_COPIED_SUCCESS));
            setIsToastVisible(true);
        });
        setTokenCopied(true);
    };
    const handleAction = () => {
        if (settingsChanged) {
            setToastMessage(t(TranslationsKeys.SETTINGS_AUTH_CHANGES_SAVED));
            setIsToastVisible(true);
            setTimeout(onAction, 2000);
        }
        else {
            onAction();
        }
    };
    const handleGenerateToken = () => {
        setIsLoading(true);
        if (nonce !== undefined && nonce !== null && nonce.length !== 0) {
            setShowRegenerateWarning(true);
        }
        createScimToken('primary');
    };
    return (React.createElement(React.Fragment, null,
        React.createElement(Modal, { isOpen: true, onDismiss: handleAction, size: ModalSize.MEDIUM3, title: t(TranslationsKeys.SETTINGS_MODAL_TITLE), className: styles.settingsModal, confirmButton: {
                text: t('CLOSE'),
                onConfirm: handleAction,
            }, isContentLoading: getScimTokenLoading, "data-testid": "settings-modal" },
            React.createElement(Vertical, { className: styles.settingsBody },
                React.createElement(Typography, { variant: "h3", className: styles.settingsSectionTitle, noMargin: true }, t(TranslationsKeys.PROVISIONING_HEADER)),
                React.createElement(Horizontal, { vAlignContent: "center", spacing: "a", className: styles.settingsDescriptionRow },
                    React.createElement(Typography, { variant: "body-normal", className: styles.settingsDescription, noMargin: true }, t(TranslationsKeys.SETTINGS_PROVISIONING_HEADING_SUBTITLE)),
                    React.createElement(Link, { className: styles.settingsLearnMore, href: learnMore?.url, target: learnMore?.newWindow ? '_blank' : undefined, rel: "noreferrer", tabIndex: 0 }, t(TranslationsKeys.SECURITY_LEARN_MORE))),
                React.createElement(Typography, { variant: "h3", className: styles.settingsSubHeading, noMargin: true }, t(TranslationsKeys.PROVISIONING_FORM_TITLE)),
                React.createElement(Vertical, { className: styles.settingsFieldGroup },
                    React.createElement(Typography, { variant: "body-normal", className: styles.settingsFieldLabel, noMargin: true }, t(TranslationsKeys.PROVISIONING_BASE_URL)),
                    React.createElement(Typography, { variant: "caption", className: styles.settingsFieldHint, noMargin: true }, t(TranslationsKeys.SETTINGS_SCIM_BASEURL_INFO)),
                    React.createElement(Horizontal, { className: styles.settingsUrlRow },
                        React.createElement(Input.Text, { value: scimBaseUrl, readOnly: true, ariaLabel: t(TranslationsKeys.PROVISIONING_BASE_URL), className: styles.settingsUrlInput }),
                        React.createElement(Button, { text: urlCopied
                                ? t(TranslationsKeys.SETTINGS_COPIED)
                                : t(TranslationsKeys.SETTINGS_SCIM_PROVISIONING_COPY_BUTTON), type: "tertiary", onClick: handleCopyUrl, className: urlCopied
                                ? styles.settingsCopiedButtonWrapper
                                : styles.settingsCopyUrlButton, "data-testid": "settings-copy-url" }))),
                React.createElement(Vertical, { className: styles.settingsTokenSection },
                    React.createElement(Vertical, { className: styles.settingsFieldLabelGroup },
                        React.createElement(Typography, { variant: "body-normal", className: styles.settingsFieldLabel, noMargin: true }, t(TranslationsKeys.SETTINGS_PROVISIONING_ACTION_TITLE)),
                        React.createElement(Typography, { variant: "caption", className: styles.settingsFieldHint, noMargin: true }, t(tokenUrl
                            ? TranslationsKeys.SETTINGS_SCIM_GENERATE_TOKEN_PRESENTED_INFO
                            : TranslationsKeys.SETTINGS_SCIM_GENERATE_TOKEN_INFO))),
                    showRegenerateError && (React.createElement(Alert.Banner, { message: errorMessage ||
                            t(TranslationsKeys.SETTINGS_SCIM_ERROR_DEFAULT), isVisible: true, type: BannerAlertType.ERROR, showCloseButton: false, className: styles.settingsWarningBanner })),
                    tokenUrl && (React.createElement(Horizontal, { className: styles.settingsUrlRow },
                        React.createElement(Input.Text, { value: tokenUrl, readOnly: true, ariaLabel: t(TranslationsKeys.PROVISIOINING_ACTION_TITLE), className: styles.settingsUrlInput }),
                        React.createElement(Button, { text: tokenCopied
                                ? t(TranslationsKeys.SETTINGS_COPIED)
                                : t(TranslationsKeys.SETTINGS_SCIM_PROVISIONING_COPY_BUTTON), type: "tertiary", onClick: handleCopyToken, className: tokenCopied
                                ? styles.settingsCopiedButtonWrapper
                                : styles.settingsCopyUrlButton, "data-testid": "settings-copy-token" }))),
                    React.createElement(Button, { text: t(TranslationsKeys.PROVISIOINING_GENERATE_BUTTON), type: "secondary", onClick: handleGenerateToken, className: styles.settingsGenerateTokenButton, "data-testid": "settings-generate-token" })))),
        React.createElement(GlobalAlert.Toast, { "data-testid": "settings-toast", message: toastMessage, isVisible: isToastVisible, onClose: () => setIsToastVisible(false), autoHide: true })));
};
//# sourceMappingURL=settings-modal.container.js.map